"use client";

import { Command } from "cmdk";
import { Calendar, CalendarPlus, Check, Search } from "lucide-react";
import { requestLeave } from "@/lib/navigation/leave-guard";
import { useParams, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventOptions } from "@/features/enterprise-sidebar/_logic/EventOptionsContext";
import { useResolvedEventId } from "@/features/enterprise-sidebar/_hooks/useResolvedEventId";
import { useOptionalSupportChat } from "@/features/support-chat/_hooks/useOptionalSupportChat";
import { changeOrganization } from "@/lib/enterprise/active-org-actions";
import { useToast } from "@/components/providers/FetchToastProvider";
import { modalStack } from "@/fetch-ui/FetchModal";
import { CHAT_PATH, EMPTY_HELP, EMPTY_TITLE, GROUP_LABELS, GROUP_ORDER, ROOT_PLACEHOLDER } from "../_logic/constants";
import { resolveSelection } from "../_logic/resolve-selection";
import { buildActionItems, buildSecondaryActions } from "../_logic/actions";
import { buildCompareHref, canCompare, filterVenues, selectionHint, toggleVenue } from "../_logic/compare-selection";
import { usePaletteAnalytics } from "../_hooks/usePaletteAnalytics";
import { PAGES, PAGE_IDS, popPage, shouldPopOnBackspace, type PageId } from "../_logic/pages";
import { useComparableProposals } from "../_hooks/useComparableProposals";
import { useProposalSearch } from "../_hooks/useProposalSearch";
import { rankItems } from "../_logic/rank";
import { RECENT_KEY_PREFIX, pushRecentId, readRecentIds, resolveRecents } from "../_logic/recents";
import { CHAT_FALLBACK_ICON, buildDestinationItems, buildEventItems } from "../_logic/registry";
import { usePlatformModifier } from "../_hooks/usePlatformModifier";
import { useCommandPaletteStore } from "../_logic/store";
import type { PaletteContext, PaletteItem, PaletteSurface } from "../_logic/types";

interface CommandPaletteProps {
  /** Server-derived. Gates items like Payments (see ENG-770). */
  isNowadaysAdmin: boolean;
  /** Which mount this is. Gates the rows a surface cannot service (ENG-835). */
  surface: PaletteSurface;
}

/**
 * The command palette dialog: one panel, opened by Cmd+K or the sidebar's
 * magnifying glass, that searches destinations, the user's events, and runs
 * actions.
 *
 * Mounted from the Enterprise shell on every dashboard route, and from
 * `StandaloneCommandPalette` on `/create` (ENG-835). `surface` says which, and
 * rows a surface cannot service are disabled rather than hidden.
 *
 * @param props - `isNowadaysAdmin` and `surface`, both read by items' `requires`.
 * @returns The palette dialog.
 */
/**
 * Where a chosen row sat in the ranked list, or null when it was not in it.
 *
 * The Recent group is resolved from every available item, while the ranked list
 * at rest drops finished and cancelled events, so a Recent row can legitimately
 * have no rank. Reporting -1 for that would put a number in the analytics that
 * looks like a position and is not one.
 */
const rankOf = (results: PaletteItem[], item: PaletteItem): number | null => {
  const index = results.findIndex((entry) => entry.id === item.id);
  return index === -1 ? null : index;
};

export const CommandPalette = ({ isNowadaysAdmin, surface }: CommandPaletteProps) => {
  // Hooks
  const router = useRouter();
  // Every destination the palette offers leaves the current page, so one
  // guarded primitive rather than a check at each of the six call sites.
  const navigate = useCallback(
    (href: string) => {
      const go = () => router.push(href);
      if (requestLeave(go)) go();
    },
    [router]
  );
  const params = useParams();
  const eventOptions = useEventOptions();
  const resolvedEventId = useResolvedEventId();
  // Optional, not required: the palette mounts on `/create` as well as the
  // dashboard, and only the dashboard has a `SupportChatProvider` (ENG-835).
  // Destructured rather than held whole: the provider re-memoizes its context
  // value on every message, open/close and status change, and depending on the
  // object rebuilt `context`, `handleSelect` and the Cmd+1..N keydown effect on
  // each one. `openWidget` itself is a stable `useCallback(..., [])`.
  const supportChat = useOptionalSupportChat();
  const openWidget = supportChat?.openWidget;
  const { showToast } = useToast();
  const isOpen = useCommandPaletteStore((state) => state.isOpen);
  const openedFrom = useCommandPaletteStore((state) => state.openedFrom);
  const close = useCommandPaletteStore((state) => state.close);
  const setPendingChatQuery = useCommandPaletteStore((state) => state.setPendingChatQuery);
  const modifier = usePlatformModifier();

  // States
  const [query, setQuery] = useState("");
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [pages, setPages] = useState<PageId[]>([]);
  // The command that asked for an event, held while the picker is up so it can
  // be finished with the answer rather than making the user retype it.
  const [pendingItem, setPendingItem] = useState<PaletteItem | null>(null);
  // An event the user explicitly chose, which outranks whatever the route says
  // for the rest of this palette session. Held separately from `pendingItem`
  // because it has to outlive it: a command that pushes a page (Compare) needs
  // the chosen event while that page is open, and clearing it with the pending
  // command left the page with no event at all.
  const [scopeOverride, setScopeOverride] = useState<string | null>(null);
  const [selectedVenues, setSelectedVenues] = useState<number[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Derived values
  // Resolved the same way the sidebar resolves it (URL, then this tab's last
  // event, validated against the user's options), so the palette can never act
  // on a different event than the sidebar is showing. When it comes back null,
  // an event-scoped command asks rather than guessing.
  //
  // Not on `/create`. `deriveSidebarMode` has no entry for that path so it falls
  // through to "event" mode, which kept the tab's remembered event alive: the
  // chip read "Acting on Q3 Sales Kickoff" while the user filled in a form for a
  // different event, and event-scoped rows navigated to that old event instead
  // of asking. Starting a new event is an exit from event mode, the same way an
  // org route is. An event the user picks here explicitly still wins.
  const eventId = scopeOverride ?? (surface === "create" ? null : resolvedEventId);

  const context = useMemo<PaletteContext>(
    () => ({
      eventId,
      isNowadaysAdmin,
      surface,
      navigate,
      pushPage: (page: string) => setPages((current) => [...current, page as PageId]),
      openSupportChat: () => openWidget?.(),
      canOpenSupportChat: !!openWidget,
      changeOrganization: () => {
        // Clears the active-org cookie so the layout re-resolves into the
        // full-page picker, the same path the sidebar's menu item takes.
        void changeOrganization().then((result) => {
          if (!result.success) {
            showToast(result.error, "error");
            return;
          }
          router.refresh();
        });
      },
    }),
    [eventId, isNowadaysAdmin, navigate, openWidget, router, showToast, surface]
  );

  const actionItems = useMemo(() => buildActionItems(), []);
  const secondaryItems = useMemo(() => buildSecondaryActions(), []);

  const allItems = useMemo(
    () => [...actionItems, ...secondaryItems, ...buildDestinationItems(), ...buildEventItems(eventOptions)],
    [actionItems, secondaryItems, eventOptions]
  );

  // Event-scoped items stay visible with no event resolved. Hiding them was
  // hiding the product from anyone standing on an org page: "Proposals" should
  // be findable from anywhere, it just has to ask which event. The asking is
  // handled at selection time, so nothing can navigate without a scope.
  const availableItems = allItems;

  const isSearching = query.trim().length > 0;

  const currentPageId = pages.length > 0 ? pages[pages.length - 1] : null;
  const currentPage = currentPageId ? PAGES[currentPageId] : null;
  const isComparePage = currentPage?.id === PAGE_IDS.compare;
  const isPickEventPage = currentPage?.id === PAGE_IDS.pickEvent;
  const scopeLabel = eventOptions.find((option) => option.value === eventId)?.label ?? null;

  const { venues, isLoading, error } = useComparableProposals(eventId, isComparePage);
  // Christina's ask: typing a hotel name should find the hotel, not nothing.
  // Only while the root list is actually being searched, so browsing the palette
  // costs no requests.
  const proposalSearch = useProposalSearch(eventId, query, isOpen && isSearching && !currentPage);
  const analytics = usePaletteAnalytics();

  // Both nested pages run the query through the same matcher the root list uses.
  // Before this they ignored it: every event stayed listed while the user typed
  // one event's name, and the highlight stayed on whatever was first, so Enter
  // acted on an event they had not asked for.
  const pickEventItems = useMemo(() => rankItems(buildEventItems(eventOptions), query), [eventOptions, query]);
  const visibleVenues = useMemo(() => filterVenues(venues, query), [query, venues]);

  // Quick actions that apply here, in accelerator order. Cmd+1..Cmd+N fires them
  // without arrowing, which is the difference between a quick action and a row
  // that happens to sit near the top.
  // Gated rows are excluded, not just skipped: `availableActions` is both the
  // Cmd+1..N index and the source of the badge numbers, so leaving a disabled
  // row in it made Cmd+1 a dead key on `/create` and started the visible badges
  // at Cmd+2.
  const availableActions = useMemo(
    () => availableItems.filter((item) => item.group === "actions" && (!item.requires || item.requires(context))),
    [availableItems, context]
  );

  // Only at rest. While searching, relevance decides the order and a Recent
  // group would just repeat rows the query already surfaced.
  const recentItems = useMemo(
    () => (isSearching ? [] : resolveRecents(availableItems, recentIds)),
    [availableItems, isSearching, recentIds]
  );

  // At rest the event list is your live events. A finished or cancelled event is
  // still findable the moment you search for it, it just does not pad a list
  // you are browsing. Hiding them from search too would repeat the mistake this
  // whole project exists to fix.
  const restingItems = useMemo(
    () => availableItems.filter((item) => item.group !== "events" || (item.bucket ?? "active") === "active"),
    [availableItems]
  );

  const rankedItems = useMemo(
    () => rankItems(isSearching ? availableItems : restingItems, query),
    [availableItems, isSearching, query, restingItems]
  );

  // Proposal rows are appended rather than ranked with the rest: they arrive
  // already narrowed by the server's own venue-name search, and re-scoring them
  // against a static registry would only demote an exact hotel name.
  const results = useMemo(() => [...rankedItems, ...proposalSearch.items], [proposalSearch.items, rankedItems]);

  // A disabled row is rendered but not selectable: cmdk's own valid-item
  // selector is `[cmdk-item]:not([aria-disabled="true"])`, so arrow keys skip it
  // and Enter never reaches it. Counting it as a result therefore suppressed the
  // handoff row while leaving nothing to press, which is the dead-Enter bug this
  // feature already fixed once. Searching "support" on `/create` hit exactly
  // that: one greyed row, no chat handoff, Enter did nothing.
  const selectableRankedCount = useMemo(
    () => rankedItems.filter((item) => !item.requires || item.requires(context)).length,
    [context, rankedItems]
  );
  const selectableResultCount = selectableRankedCount + proposalSearch.items.length;

  // Effects for the zero-result log live with the other effects below; this is
  // just the condition they watch.
  // A query is only a miss once the proposal search has also settled. Counting
  // it sooner both flashed the handoff row between keystrokes and put false
  // entries in the zero-result log this palette exists to fill.
  //
  // A failed search is not a miss either. `reportEmptyQuery` is deduped per
  // org+query for the session, so one outage would write a query into the
  // zero-result log permanently and then suppress the real signal once the
  // action recovered. The handoff row below has its own condition and survives
  // the outage without this term.
  // Deliberately `results`, not the selectable count: this drives the "No
  // results" copy and `reportEmptyQuery`, and a query that matched a row the
  // user can see did not find nothing. Using the selectable count here printed
  // "No results" above a visible row and logged the query as a registry miss.
  // `showChatHandoff` is the one that needs something pressable.
  const isEmptySearch =
    isSearching && !currentPage && results.length === 0 && !proposalSearch.isLoading && !proposalSearch.error;

  // Keyed on the ranked list alone, never on `results`, and rendered in a fixed
  // slot below the groups so venue rows landing cannot unmount it.
  //
  // Two bugs sit on either side of this. Waiting for the venue search left a
  // query with no registry match zero selectable rows, so Enter did nothing for
  // a debounce plus a round trip. Then dropping the wait unmounted the row when
  // venue rows arrived: cmdk re-selects the first item whenever the selected one
  // unmounts (`node_modules/cmdk` calls its `W()` from the item's own cleanup),
  // so the user read "Open AI chat" and Enter took them to a proposals grid.
  // Registry matches only change when the query does, so this row now only
  // unmounts on a keystroke, which is the one time reassigning is correct.
  const showChatHandoff = isSearching && !currentPage && selectableRankedCount === 0;

  // One line, announced politely, for three states that all arrive after the
  // keystroke that asked for them. Screen readers get no other signal that six
  // options appeared above what was being read (WCAG 4.1.3).
  const proposalStatus =
    !isSearching || currentPage
      ? ""
      : proposalSearch.error
        ? proposalSearch.error
        : proposalSearch.isLoading
          ? "Searching proposals..."
          : proposalSearch.items.length > 0
            ? `${proposalSearch.items.length} ${proposalSearch.items.length === 1 ? "proposal" : "proposals"} found`
            : "";

  const groups = useMemo(() => {
    const base = GROUP_ORDER.map((group) => ({
      group,
      items: results.filter((item) => item.group === group),
    })).filter((entry) => entry.items.length > 0);

    if (recentItems.length === 0) return base;
    // Recent duplicates rows that also appear in their home group, which is what
    // Linear and Supabase both do: you scan the top four, and the full list is
    // still where you expect it if you'd rather browse.
    return [{ group: "recent" as const, items: recentItems }, ...base];
  }, [recentItems, results]);

  // Handlers
  const handleSelect = useCallback(
    (item: PaletteItem) => {
      const outcome = resolveSelection(item, context);

      // An event-scoped command with no event asks which one, then finishes
      // itself with the answer. Guessing here is the one failure mode that is
      // both silent and wrong: the user lands on another event's data.
      if (outcome.kind === "unresolvable") {
        setPendingItem(item);
        setScopeOverride(null);
        setPages((current) => [...current, PAGE_IDS.pickEvent]);
        return;
      }

      // Second line of defence behind the `requires` filter on
      // `availableActions`. cmdk already makes a disabled row inert to click and
      // to Enter, and the accelerators no longer index one, so nothing reaches
      // here today. Kept because a gated row reported as a chosen row would put
      // selections in the analytics that never happened, and the only thing
      // stopping that is a filter someone could reasonably simplify away.
      if (outcome.kind === "blocked") return;

      analytics.trackSelected({
        itemId: item.id,
        group: item.group,
        query,
        // Where the row sat in what the user was looking at. A destination that
        // is only ever taken from rank 8 is one the keywords are failing.
        // Null rather than -1 when the row was not in the ranked list at all:
        // a Recent row can be an event the resting list filters out, and a
        // wrong number in an analytics stream is worse than an absent one.
        rank: rankOf(results, item),
      });

      if (outcome.recordsRecent) pushRecentId(item.id);
      if (outcome.closes) close();

      if (outcome.kind === "push") context.pushPage(outcome.page);
      if (outcome.kind === "action" && item.run.kind === "action") item.run.fn(context);
      if (outcome.kind === "navigate") navigate(outcome.href);
    },
    [analytics, close, context, navigate, query, results]
  );

  /** Finishes the command that was waiting on an event. */
  const handlePickEvent = useCallback(
    (chosenEventId: string) => {
      const item = pendingItem;
      setScopeOverride(chosenEventId);
      setPendingItem(null);
      setPages(popPage);
      if (!item) return;

      const outcome = resolveSelection(item, { ...context, eventId: chosenEventId });
      if (outcome.kind === "push") {
        setPages((current) => [...current, outcome.page as PageId]);
        return;
      }
      if (outcome.kind !== "navigate") return;
      if (outcome.recordsRecent) pushRecentId(item.id);
      close();
      navigate(outcome.href);
    },
    [close, context, navigate, pendingItem]
  );

  const handlePop = useCallback(() => setPages(popPage), []);

  /**
   * Hands a failed query to the AI chat, ready to ask.
   *
   * Nothing is sent. `NewChatView` seeds the composer with this text and puts
   * the caret after it, and the user presses Enter. Anna asked for prefill over
   * an automatic send, and it is the right call: a palette query is a few
   * keywords, not a question, so the extra keypress is where it becomes one.
   *
   * The query travels in the store rather than in the URL. The palette opens
   * over the chat page too, and pushing the route you are already on does not
   * deliver a new search param to an already-mounted client tree, so the URL
   * version dropped the query silently in exactly that case.
   */
  const handleChatHandoff = useCallback(() => {
    analytics.trackChatHandoff(query);
    setPendingChatQuery(query.trim());
    close();
    navigate(CHAT_PATH);
  }, [analytics, close, navigate, query, setPendingChatQuery]);

  const handleCompareConfirm = useCallback(() => {
    const href = buildCompareHref(eventId, selectedVenues);
    if (!href) return;
    close();
    navigate(href);
  }, [close, eventId, navigate, selectedVenues]);

  // Effects
  // Reset the query on every open, and re-read recents then rather than on
  // mount so a selection made in this tab is reflected the next time.
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setPages([]);
    setSelectedVenues([]);
    setPendingItem(null);
    setScopeOverride(null);
    setRecentIds(readRecentIds());
  }, [isOpen]);

  // A query typed on one page should not survive into the next, where it would
  // filter a list the user has not typed anything into yet.
  //
  // Keyed on which page is on top rather than on how deep the stack is:
  // `handlePickEvent` pops the picker and pushes Compare in the same commit, so
  // the depth is 1 before and 1 after and this never re-ran. That is how "imex"
  // ended up sitting in the box on Compare after picking an event.
  useEffect(() => {
    setQuery("");
  }, [currentPageId]);

  // Escape is Radix's, but the overlays underneath run their own document
  // listeners and only stand down for whatever `modalStack` says is on top.
  // Without this, one Escape from the palette also closed the agenda planner
  // beneath it on `/create`.
  useEffect(() => {
    if (!isOpen) return;
    modalStack.push(close);
    return () => modalStack.remove(close);
  }, [close, isOpen]);

  // Whether anyone actually presses the hotkey, versus clicking the magnifying
  // glass, is the open question the store's `openedFrom` was added for. Reported
  // on open rather than on mount: the component is mounted on every dashboard
  // page whether or not it is ever used.
  useEffect(() => {
    if (!isOpen || !openedFrom) return;
    analytics.trackOpened(openedFrom);
  }, [analytics, isOpen, openedFrom]);

  // The zero-result log. Reported from an effect rather than from the input
  // handler so it watches what is actually on screen: a query only counts as a
  // miss once the ranked list has settled on nothing, and any result arriving
  // cancels it.
  useEffect(() => {
    if (!isOpen) return;
    if (isEmptySearch) {
      analytics.reportEmptyQuery(query);
      return;
    }
    analytics.cancelEmptyQuery();
  }, [analytics, isEmptySearch, isOpen, query]);

  // Closing mid-type is a miss too, and a quiet one: the user gave up rather
  // than waiting out the settle delay.
  useEffect(() => {
    if (isOpen) return;
    analytics.cancelEmptyQuery();
  }, [analytics, isOpen]);

  // Put the caret back in the input whenever the page changes. Selecting a row
  // with the mouse leaves focus on the list, so the first thing typed on a
  // freshly pushed page went nowhere at all.
  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
  }, [currentPageId, isOpen]);

  // Cmd+1..Cmd+N fire the quick actions directly. Bound only while the palette
  // is open, so these digits stay free everywhere else in the app.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;
      const index = Number(event.key) - 1;
      if (!Number.isInteger(index) || index < 0) return;
      const action = availableActions[index];
      if (!action) return;

      event.preventDefault();
      handleSelect(action);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [availableActions, handleSelect, isOpen]);

  // Render
  return (
    <Command.Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      label="Command palette"
      // cmdk's own filter is an ordered subsequence match over one concatenated
      // string, which both invents matches and misses multi-word queries. We
      // supply the ranked list ourselves instead — see `rank.ts`.
      shouldFilter={false}
      // `className` lands on cmdk's inner root, INSIDE the dialog content — not
      // on a wrapper. Positioning it there collapses the content box to zero
      // height and the panel renders invisibly behind the overlay.
      // Literal, not `z-[${PALETTE_Z_INDEX}]`: Tailwind extracts class names
      // statically, so an interpolated one compiles to no z-index at all. The
      // constant carries the reasoning and a test pins the two together.
      overlayClassName="fixed inset-0 z-[100000] bg-gray-900/25"
      contentClassName="fixed left-1/2 top-[15vh] z-[100000] flex w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-gray-900/5"
    >
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3.5">
        <Search className="size-4 shrink-0 text-gray-400" />
        {currentPage && (
          <span className="shrink-0 rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-600">
            {currentPage.title}
          </span>
        )}
        {!currentPage && scopeLabel && (
          <span
            className="max-w-[10rem] shrink-0 truncate rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500"
            title={`Acting on ${scopeLabel}`}
          >
            {scopeLabel}
          </span>
        )}
        <Command.Input
          ref={inputRef}
          value={query}
          onValueChange={setQuery}
          placeholder={currentPage?.placeholder ?? ROOT_PLACEHOLDER}
          onKeyDown={(event) => {
            if (event.key !== "Backspace") return;
            if (!shouldPopOnBackspace(pages, query)) return;
            event.preventDefault();
            handlePop();
          }}
          className="w-full bg-transparent text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400"
        />
      </div>

      {/* Outside `Command.List`, which cmdk renders as `role="listbox"`. A listbox
          may own only options and groups, so a bare paragraph in there is invalid
          owned content that assistive tech may prune or misreport. */}
      <div
        role="status"
        aria-live="polite"
        className={proposalStatus ? "border-b border-gray-100 px-4 py-2 text-xs font-medium text-gray-500" : "sr-only"}
      >
        {proposalStatus}
      </div>

      <Command.List aria-busy={proposalSearch.isLoading} className="max-h-[min(24rem,60vh)] overflow-y-auto py-2">
        {isPickEventPage ? (
          <>
            {eventOptions.length === 0 && (
              <Command.Item
                value="create your first event"
                onSelect={() => {
                  close();
                  navigate("/create");
                }}
                className="mx-2 flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-gray-900 data-[selected=true]:bg-indigo-50"
              >
                <CalendarPlus className="size-4 shrink-0 text-gray-400" />
                <span>Create your first event</span>
              </Command.Item>
            )}
            {eventOptions.length > 0 && pickEventItems.length === 0 && (
              <p className="px-4 py-6 text-center text-sm font-medium text-gray-400">No events match that</p>
            )}
            {/* Built by the registry rather than mapped from `eventOptions` here,
                so a row means the same thing on this page as in the main list:
                same order, and Past and Cancelled carry their tag instead of
                sitting among the live events unmarked. */}
            {pickEventItems.map((item) => (
              <Command.Item
                key={item.id}
                value={item.id}
                onSelect={() => item.eventId && handlePickEvent(item.eventId)}
                className="mx-2 flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-gray-900 data-[selected=true]:bg-indigo-50"
              >
                <Calendar className="size-4 shrink-0 text-gray-400" />
                <span className="truncate">{item.label}</span>
                <span className="ml-auto shrink-0 pl-3 text-xs font-medium text-gray-400">{item.hint}</span>
              </Command.Item>
            ))}
          </>
        ) : isComparePage ? (
          <>
            {isLoading && (
              <p className="px-4 py-6 text-center text-sm font-medium text-gray-400">Loading proposals...</p>
            )}
            {error && <p className="px-4 py-6 text-center text-sm font-medium text-gray-400">{error}</p>}
            {!isLoading && !error && venues.length === 0 && (
              <p className="px-4 py-6 text-center text-sm font-medium text-gray-400">No proposals to compare yet</p>
            )}
            {/* A selection already made stays selected even when the query hides
                its row, so narrowing the list to find the second venue cannot
                silently drop the first. The footer count is the source of truth. */}
            {!isLoading && !error && venues.length > 0 && visibleVenues.length === 0 && (
              <p className="px-4 py-6 text-center text-sm font-medium text-gray-400">No proposals match that</p>
            )}
            {!isLoading &&
              !error &&
              visibleVenues.map((venue) => {
                const isSelected = selectedVenues.includes(venue.venueStaticId);
                return (
                  <Command.Item
                    key={venue.venueStaticId}
                    value={`${venue.venueName} ${venue.location ?? ""}`}
                    onSelect={() => setSelectedVenues((current) => toggleVenue(current, venue.venueStaticId))}
                    className="mx-2 flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-gray-900 data-[selected=true]:bg-indigo-50"
                  >
                    <span
                      className={`flex size-4 shrink-0 items-center justify-center rounded border ${
                        isSelected ? "border-indigo-600 bg-indigo-600 text-white" : "border-gray-300"
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected && <Check className="size-3" />}
                    </span>
                    <span className="truncate">{venue.venueName}</span>
                    {venue.location && (
                      <span className="ml-auto shrink-0 pl-3 text-xs font-medium text-gray-400">{venue.location}</span>
                    )}
                  </Command.Item>
                );
              })}
          </>
        ) : (
          <>
            {!showChatHandoff && (
              <Command.Empty className="px-4 py-6 text-center text-sm font-medium text-gray-400">
                {EMPTY_TITLE}
              </Command.Empty>
            )}

            {groups.map(({ group, items }) => (
              <Command.Group
                key={group}
                heading={GROUP_LABELS[group]}
                className="px-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-gray-400"
              >
                {items.map((item) => {
                  const Icon = item.icon;
                  const isDisabled = Boolean(item.requires && !item.requires(context));
                  // An event-scoped row with no event resolved will ask which one
                  // before it goes anywhere. Say so: an unmarked row that behaves
                  // differently from its neighbours is worse than no row.
                  const willAskForEvent = item.scope === "event" && !eventId;
                  return (
                    <Command.Item
                      key={`${group}-${item.id}`}
                      value={group === "recent" ? `${RECENT_KEY_PREFIX}${item.id}` : item.id}
                      disabled={isDisabled}
                      // The reason is a trailing span, and cmdk drives screen
                      // readers through `aria-activedescendant`, which a disabled
                      // row can never become. Folding it into the accessible name
                      // is the only way the row and its reason are read together.
                      aria-label={
                        isDisabled && item.disabledReason ? `${item.label}, ${item.disabledReason}` : undefined
                      }
                      onSelect={() => handleSelect(item)}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-gray-900 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-gray-400 data-[selected=true]:bg-indigo-50"
                    >
                      <Icon className="size-4 shrink-0 text-gray-400" />
                      <span className="truncate">{item.label}</span>
                      {willAskForEvent && !isDisabled ? (
                        <span className="ml-auto shrink-0 pl-3 text-xs font-medium text-gray-400">Pick event</span>
                      ) : group === "actions" && !isDisabled && modifier ? (
                        <kbd className="ml-auto shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">
                          {modifier.symbol}
                          {availableActions.indexOf(item) + 1}
                        </kbd>
                      ) : (item.disabledReason && isDisabled) || item.hint ? (
                        <span className="ml-auto shrink-0 pl-3 text-xs font-medium text-gray-400">
                          {isDisabled ? item.disabledReason : item.hint}
                        </span>
                      ) : null}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            ))}

            {/* A miss is a door, not a dead end. The palette knows the query
                failed and the chat is the only thing in the product that can
                answer an arbitrary question, so hand it over rather than
                shrugging. Rendered instead of `Command.Empty` because this row
                has to be selectable, and cmdk's empty slot is not.

                Last, not first, so that venue rows arriving mid-query insert
                above it rather than replacing it. See `showChatHandoff`. */}
            {showChatHandoff && (
              <div className="px-2 pb-1 pt-2">
                {isEmptySearch && (
                  <p className="px-2 pb-2 text-sm font-medium text-gray-400">
                    {EMPTY_TITLE}. {EMPTY_HELP}
                  </p>
                )}
                <Command.Item
                  value="ask-ai-chat"
                  onSelect={handleChatHandoff}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-gray-900 data-[selected=true]:bg-indigo-50"
                >
                  <CHAT_FALLBACK_ICON className="size-4 shrink-0 text-gray-400" />
                  <span className="truncate">Open AI chat about &ldquo;{query.trim()}&rdquo;</span>
                  <span className="ml-auto shrink-0 pl-3 text-xs font-medium text-gray-400">&crarr; enter</span>
                </Command.Item>
              </div>
            )}
          </>
        )}
      </Command.List>

      <div className="flex items-center gap-4 border-t border-gray-200 px-4 py-2.5 text-[11px] font-medium text-gray-400">
        {isPickEventPage ? (
          <>
            <span>&crarr; select</span>
            <span>&#9003; back</span>
            {pendingItem && <span className="ml-auto truncate">then: {pendingItem.label.replace(/\.\.\.$/, "")}</span>}
          </>
        ) : isComparePage ? (
          <>
            <span>&crarr; toggle</span>
            <span>&#9003; back</span>
            <span className="ml-auto flex items-center gap-2">
              {selectionHint(selectedVenues)}
              <button
                type="button"
                disabled={!canCompare(selectedVenues)}
                onClick={handleCompareConfirm}
                className="rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white disabled:bg-gray-200 disabled:text-gray-400"
              >
                Compare
              </button>
            </span>
          </>
        ) : (
          <>
            <span>&uarr;&darr; move</span>
            <span>&crarr; select</span>
            <span>esc close</span>
          </>
        )}
      </div>
    </Command.Dialog>
  );
};
